import Vue from 'vue'
import VueRouter from 'vue-router'
import store from '@/store'
import Home from '../views/home/Home.vue'
import Error404 from '@/views/core/404.vue'
import NoPerm from '@/views/core/NoPerm.vue'

Vue.use(VueRouter)

const routes = [
  {
    path: '/',
    name: 'Home',
    component: Home,
    meta: { displayName: 'Dashboard' }
  },
  {
    path: '/login',
    name: 'Login',
    meta: { noAuthRequired: true },
    component: () =>
      import(/*webpackChunkName: "login"*/ '@/views/core/Login.vue')
  },
  {
    path: '/shop',
    name: 'Shop',
    meta: { displayName: 'Shop' },
    component: () => import('@/views/core/Shop.vue')
  },
  {
    path: '/settings/crm',
    name: 'Einstellungen: CRM',
    component: () => import('@/views/settings/Settings.vue'),
    props: { app: 'crm', displayName: 'CRM' }
  },
  {
    path: '/settings/calv2',
    name: 'Einstellungen: Kalender',
    component: () => import('@/views/settings/Settings.vue'),
    props: { app: 'cal', displayName: 'Kalender' }
  },
  {
    path: '/cal',
    name: 'Calendar',
    meta: { displayName: 'Kalender' },

    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    component: () =>
      import(/* webpackChunkName: "calendar" */ '@/views/calendar/Calendar.vue')
  },
  {
    path: '/calv2',
    name: 'Calendar-v2',
    meta: { displayName: 'Kalender', version: '✨ v2' },

    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    component: () =>
      import(
        /* webpackChunkName: "calendar_v2" */ '@/views/calendar_v2/Calendar_v2.vue'
      ),
    beforeEnter: (to, from, next) => {
      if (
        store.getters.settings_cal !== undefined &&
        store.getters.locations !== undefined &&
        store.getters.scopes !== undefined
      ) {
        next()
      } else {
        next('/calv2/loading')
      }
    }
  },
  {
    path: '/calv2/loading',
    name: 'Calender-v2 Loading',
    component: () =>
      import(
        /* webpackChunkName: "calendar_v2" */ '@/views/calendar_v2/Loading.vue'
      )
  },
  {
    path: '/bi/revenue',
    name: 'Umsatzstatistik',
    component: () =>
      import(/* webpackChunkName: "calendar_v2" */ '@/views/bi/Revenue.vue')
  },
  {
    path: '/bi/keyfigures',
    name: 'Kennzahlen',
    component: () =>
      import(/* webpackChunkName: "calendar_v2" */ '@/views/bi/KeyFigures.vue')
  },
  {
    path: '/task',
    name: 'Task',
    meta: { displayName: 'Aufgabenverwaltung' },
    component: () =>
      import(/* webpackChunkName: "calendar_v2" */ '@/views/task/Task.vue')
  },
  {
    path: '/task/new',
    name: 'Aufgabe erstellen',
    component: () =>
      import(/* webpackChunkName: "AddTask" */ '@/views/task/AddTask.vue')
  },
  {
    path: '/task/:lfdnr',
    name: 'TaskDetails',
    props: route => {
      const lfdnr = Number.parseInt(route.params.lfdnr, 10)
      if (Number.isNaN(lfdnr)) {
        return 0
      }
      return { lfdnr }
    },
    component: () => import('@/views/task/TaskDetailRedirect.vue')
  },
  {
    name: 'CRM',
    path: '/crm',
    meta: { displayName: 'Kundenverwaltung' },
    component: () => import(/* webpackChunkName: "crm" */ '@/views/crm/CRM.vue')
  },
  {
    path: '/crm/addresses',
    redirect: { name: 'CRM' },
    meta: { displayName: 'Adressen' }
  },
  {
    path: '/crm/addresses/new',
    name: 'CRM New Address',
    meta: { displayName: 'Neue Adresse' },
    component: () => import('@/views/crm/addresses/AddAddress.vue')
  },
  {
    path: '/crm/addresses/:lfdnr',
    name: 'crmAddress',
    meta: { displayName: 'Adressen' },
    props: route => {
      const lfdnr = Number.parseInt(route.params.lfdnr, 10)
      if (Number.isNaN(lfdnr)) {
        return 0
      }
      return { lfdnr }
    },
    component: () =>
      import(
        /* webpackChunkName: "crmAddress" */ '@/views/crm/addresses/Address.vue'
      )
  },
  {
    path: '/crm/addresses/:lfdnr/documents',
    redirect: { name: 'crmAddress', query: { tab: 'documents' } },
    meta: { displayName: 'Dokumente' },
    props: route => {
      const lfdnr = Number.parseInt(route.params.lfdnr, 10)
      if (Number.isNaN(lfdnr)) {
        return 0
      }
      return { lfdnr }
    }
  },
  {
    path: '/crm/addresses/:lfdnr/interactions',
    name: 'crmInteractions',
    redirect: {
      name: 'crmAddress',
      query: { tab: 'interactions' }
    },
    meta: { displayName: 'Interaktionen' },
    props: route => {
      const lfdnr = Number.parseInt(route.params.lfdnr, 10)
      if (Number.isNaN(lfdnr)) {
        return 0
      }
      return { lfdnr }
    }
  },
  {
    path: '/crm/addresses/:lfdnr/documents/:wf_cid',
    name: 'crmAddressDocumentDetailed',
    meta: { displayName: 'Dokument' },
    props: route => {
      const wf_cid = route.params.wf_cid
      const lfdnr = Number.parseInt(route.params.lfdnr, 10)
      if (Number.isNaN(lfdnr)) {
        return 0
      }
      return { lfdnr, wf_cid }
    },
    component: () =>
      import(
        /* webpackChunkName: "crmAddressDocumentDetailed" */ '@/views/crm/addresses/AddressDocumentDetailed.vue'
      )
  },

  {
    path: '/dev',
    name: 'DEV-Portal',
    meta: { displayName: 'Entwickler Portal' },

    component: () =>
      import(
        /* webpackChunkName: "devPortal" */ '@/views/devPortal/DevPortal.vue'
      )
  },
  {
    path: '/articles',
    name: 'Artikel',
    meta: {
      requiresPermission: true,
      displayName: 'Artikel'
    },
    component: () =>
      import(
        /* webpackChunkName: "Artikelsuche" */ '@/views/article/Article.vue'
      )
  },
  {
    path: '/articles/:articleNumber',
    name: 'Artikeldetails',
    props: route => {
      const articleNumber = route.params.articleNumber

      return { articleNumber }
    },
    component: () =>
      import(
        /* webpackChunkName: "Artikel" */ '@/views/article/ArticleView.vue'
      )
  },
  {
    name: 'Statistik',
    path: '/statistics',
    meta: { displayName: 'Statistik' },
    component: () =>
      import(
        /* webpackChunkName: "statistics" */ '@/views/statistics/STATISTICS.vue'
      ),
    children: []
  },

  {
    path: '/statistics/distributors/:lfdnr/articletypes',
    name: 'statisticsDistributorArticleTypes',
    props: route => {
      const lfdnr = Number.parseInt(route.params.lfdnr, 10)
      if (Number.isNaN(lfdnr)) {
        return 0
      }
      return { lfdnr }
    },
    component: () =>
      import(
        /* webpackChunkName: "statisticsDistributorArticleTypes" */ '@/views/statistics/distributor/ArticleTypes.vue'
      )
  },
  {
    path: '/statistics/revenue',
    name: 'statisticsRevenue',
    meta: { displayName: 'Umsatzstatisik' },
    component: () =>
      import(
        /* webpackChunkName: "statisticsDistributorArticleTypes" */ '@/views/statistics/revenue/RevenueHomepage.vue'
      )
  },
  {
    path: '/statistics/Metric',
    name: 'statisticsMetric',
    meta: { displayName: 'Kennzahlen' },
    component: () =>
      import(
        /* webpackChunkName: "statisticsDistributorArticleTypes" */ '@/views/statistics/Metric/MetricHomepage.vue'
      )
  },
  {
    path: '/statistics/customerDevelopment',
    name: 'statisticsCustomer',
    meta: { displayName: 'Kundenstatistik' },
    component: () =>
      import(
        /* webpackChunkName: "statisticsDistributorArticleCategories" */ '@/views/statistics/customer/CustomerDevelopment.vue'
      )
  },
  {
    path: '/statistics/articles',
    name: 'statisticsArticle',
    meta: { displayName: 'Artikelstatistik' },
    component: () =>
      import(
        /* webpackChunkName: "statisticsDistributorArticleCategories" */ '@/views/statistics/articleTypes/ArticleDevelopment.vue'
      )
  },
  {
    path: '/statistics/offer',
    name: 'statisticsOffer',
    meta: { displayName: 'Aufträgestatistik' },
    component: () =>
      import(
        /* webpackChunkName: "statisticsDistributorArticleCategories" */ '@/views/statistics/offer/OfferHomepage.vue'
      )
  },
  {
    path:
      '/statistics/distributors/:lfdnr/articletypes/:articleType/articlecategories',
    name: 'statisticsDistributorArticleCategories',
    props: route => {
      const articleType = route.params.articleType
      const lfdnr = Number.parseInt(route.params.lfdnr, 10)
      if (Number.isNaN(lfdnr)) {
        return 0
      }
      return { lfdnr, articleType }
    },
    component: () =>
      import(
        /* webpackChunkName: "statisticsDistributorArticleCategories" */ '@/views/statistics/distributor/ArticleCategories.vue'
      )
  },
  {
    path:
      '/statistics/crm/addresses/:lfdnr/articletypes/:articleType/articlecategories',
    name: 'statisticsCustomerArticleCategories',
    props: route => {
      const articleType = route.params.articleType
      const lfdnr = Number.parseInt(route.params.lfdnr, 10)
      if (Number.isNaN(lfdnr)) {
        return 0
      }
      return { lfdnr, articleType }
    },
    component: () =>
      import(
        /* webpackChunkName: "statisticsDistributorArticleCategories" */ '@/views/statistics/customer/articleCategory.vue'
      )
  },
  {
    path:
      '/statistics/distributors/:lfdnr/articletypes/:articleType/articlecategories/:category/articles',
    name: 'statisticsDistributorArticles',
    props: route => {
      const lfdnr = Number.parseInt(route.params.lfdnr, 10)
      if (Number.isNaN(lfdnr)) {
        return 0
      }
      const articleType = route.params.articleType

      const category = Number.parseInt(route.params.category, 10)
      if (Number.isNaN(category)) {
        return 0
      }
      return { lfdnr, articleType, category }
    },
    component: () =>
      import(
        /* webpackChunkName: "statisticsDistributorArticles" */ '@/views/statistics/distributor/Articles.vue'
      )
  },

  {
    path: '/statistics/distributors/:lfdnr/articles/search',
    name: 'statisticsArticleSearch',
    props: route => {
      const lfdnr = Number.parseInt(route.params.lfdnr, 10)
      if (Number.isNaN(lfdnr)) {
        return 0
      }
      return { lfdnr }
    },
    component: () =>
      import(
        /* webpackChunkName: "statisticsArticleSearch" */ '@/views/statistics/distributor/ArticleSearch.vue'
      )
  },

  {
    path: '/statistics/customers/:lfdnr/articles/search',
    name: 'statisticsArticleSearchRevenue',
    props: route => {
      const lfdnr = Number.parseInt(route.params.lfdnr, 10)
      if (Number.isNaN(lfdnr)) {
        return 0
      }
      return { lfdnr }
    },
    component: () =>
      import(
        /* webpackChunkName: "statisticsArticleSearch" */ '@/views/statistics/customer/ArticleSearch.vue'
      )
  },
  {
    path: '/statistics/distributors/:lfdnr/articles/:artNr',
    name: 'statisticsArticles',
    props: route => {
      const lfdnr = Number.parseInt(route.params.lfdnr, 10)
      if (Number.isNaN(lfdnr)) {
        return 0
      }

      const artNr = route.params.artNr

      return { lfdnr, artNr }
    },
    component: () =>
      import(
        /* webpackChunkName: "statisticsArticleDetailed" */ '@/views/statistics/distributor/ArticleDetailed.vue'
      )
  },
  {
    path: '/business',
    name: 'Business',
    meta: {
      displayName: 'Business'
    },
    component: () =>
      import(/* webPackChunkName: "Business" */ '@/views/business/Business.vue')
  },
  {
    path: '/notifications',
    name: 'Notification',
    meta: { displayName: 'Benachrichtigungen' },
    component: () =>
      import(
        /* webpackChunkName: "" */ '@/views/notifications/NotificationView.vue'
      )
  },
  {
    path: '/changelog',
    name: 'Changelog',
    meta: { displayName: 'Versionverlauf', noAuthRequired: true },
    component: () =>
      import(
        /* webpackChungName: "Changelog" */ '@/views/changelog/ChangelogView.vue'
      )
  },
  {
    path: '/nopermission',
    name: 'Kein Zugriff',
    meta: { displayName: 'Kein Zugriff' },

    component: NoPerm
  },
  {
    path: '*',
    name: 'Not Found',
    meta: { displayName: 'Nicht gefunden' },

    component: Error404
  }
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes
})

router.beforeEach(async (to, from, next) => {
  const loggedIn = store.getters.loggedIn
  if (to.matched.some(record => !record.meta.noAuthRequired) && !loggedIn) {
    next({ path: '/login', query: { redirectTo: to.path } })
  }
  if (to.matched.some(record => record.meta.requiresPermission)) {
    setTimeout(() => {
      var hasPermission = store.getters.availableAppPermissions
      console.log(hasPermission)
      hasPermission.forEach(function(item) {
        if (
          to.path.includes(item.appUrl) &&
          to.matched.some(record => record.meta.requiresPermission)
        ) {
          next({ name: 'Kein Zugriff' })
        }
      })
    }, 1500)
  }
  next()
})

const originalPush = VueRouter.prototype.push
VueRouter.prototype.push = function push(location, onResolve, onReject) {
  if (onResolve || onReject)
    return originalPush.call(this, location, onResolve, onReject)
  return originalPush.call(this, location).catch(err => {
    if (VueRouter.isNavigationFailure(err)) {
      // resolve err
      return err
    }
    // rethrow error
    return Promise.reject(err)
  })
}

export default router
